import { NotificationsActions } from '../../app/common/notifications/notificationsActions';
import WINDOW from '../../declarations/window.d';
import { TranslationHelpers } from '../../i18n/translationHelpers';
import ErrorRedirectService from '../../routes/error/errorRedirect.service';
import store from '../../utils/store';
import { HttpStatus } from '../httpStatus.constant';
import {
    IPayVaultAPIFailureResponse,
    IPayVaultAPISuccessResponse,
    IPayVaultElements,
    IPayVaultEvent,
    PayVault,
    PayVaultAPIResultType,
} from './payVault.interface';
import { PayVaultAPIErrorCodesConstant } from './payVaultAPIErrorCodes.constant';
import WaitPromise from '../../utils/WaitPromise';

export class PayVaultAPI {
    private payvault?: PayVault;
    private PAYVAULT_SERVICE_ID: string = '';
    private PAYVAULT_URL: string = '';

    private PAYVAULT_LOAD_WAIT: WaitPromise<boolean> = new WaitPromise<boolean>();

    public init(PAYVAULT_URL: string, PAYVAULT_SERVICE_ID: string) {
        this.PAYVAULT_URL = PAYVAULT_URL;
        this.PAYVAULT_SERVICE_ID = PAYVAULT_SERVICE_ID;
        this.getPayVault().then((payVault: PayVault) => {
            this.payvault = payVault;
        });
    }

    public async setupPayVault(payVaultElements: IPayVaultElements): Promise<PayVault> {
        return this.payvault!!.setup(payVaultElements);
    }

    public showScanBtn() {
        const scanCardBtnStyle = document.getElementById('BtnControl');
        if (this.payvault && 'openModal' in this.payvault) {
            scanCardBtnStyle?.removeAttribute('hidden');
        }
    }

    public onScanBtnClick = async () => {
        if (this.payvault && typeof this.payvault.openModal === 'function') {
            try {
                const scanModal = document.getElementById('pv-card-scan-modal-container');
                if (scanModal) scanModal.style.zIndex = '999';
                const result = await this.payvault?.openModal({
                    serviceId: this.PAYVAULT_SERVICE_ID,
                });
                if (result.resultType === 'failure' && result.errorCode === 'camera_error') {
                    store.dispatch(
                        NotificationsActions.showWarning(
                            TranslationHelpers.getFormattedMessage('error.payvault.camera_deny'),
                        ),
                    );
                }
            } catch {
                store.dispatch(
                    NotificationsActions.showWarning(
                        TranslationHelpers.getFormattedMessage('error.payvault.unavailable'),
                    ),
                );
            }
        } else {
            store.dispatch(
                NotificationsActions.showWarning(
                    TranslationHelpers.getFormattedMessage('error.payvault.unavailable'),
                ),
            );
        }
    };

    public async isPayVaultLoaded() {
        const res = await this.PAYVAULT_LOAD_WAIT.until(() => {
            const cardNumberParents = document.querySelector('#my-card-mount');
            if (cardNumberParents) {
                if (cardNumberParents.children.length > 0) {
                    return true;
                }
            }
            return false;
        });
        return res;
    }

    public cleanPayVaultLoadedCheck() {
        this.PAYVAULT_LOAD_WAIT.clean();
    }

    public getPayVault(): Promise<PayVault> {
        const payVault = WINDOW.payvault;
        return new Promise((resolve, reject) => {
            if (payVault) {
                resolve(payVault);
            } else {
                this.loadPayVault((loadedPayVault: PayVault) => {
                    resolve(loadedPayVault);
                });
            }
        });
    }

    public loadPayVault(callback: (payVault: PayVault) => any) {
        if (this.PAYVAULT_URL) {
            const scriptEl = document.createElement('script');
            scriptEl.id = 'payVaultScript';
            scriptEl.type = 'text/javascript';
            scriptEl.src = this.PAYVAULT_URL;
            scriptEl.onload = () => {
                const readyInterval = setInterval(() => {
                    const payVault = WINDOW.payvault;
                    if (payVault) {
                        clearInterval(readyInterval);
                        callback(payVault);
                    }
                }, 100);
            };
            const bodyEl = document.querySelector('body');
            if (bodyEl) {
                bodyEl.appendChild(scriptEl);
            }
        }
    }

    public destroyIframe() {
        // no built-in function exists to see if payvault has been set up in the iframe,
        // this line checks if destroy function exists, if it does, it invokes it
        this.payvault?.destroy?.();
    }

    public cardNumberEventListener(callback: Function) {
        this.payvault?.card.addEventListener(
            'cardNumber',
            'input',
            (event: { valid: Record<string, any> }) => {
                if (callback) {
                    callback(event);
                }
            },
        );
    }
    public cardNumberFocusEventListener(callback: Function) {
        this.payvault?.card.addEventListener('cardNumber', 'focus', (event: IPayVaultEvent) => {
            if (callback) {
                callback(event);
            }
        });
    }

    public cardNumberBlurEventListener(callback: Function) {
        this.payvault?.card.addEventListener('cardNumber', 'blur', (event: IPayVaultEvent) => {
            if (callback) {
                callback(event);
            }
        });
    }

    public expirationMonthEventListener(callback: Function) {
        this.payvault?.card.addEventListener('expirationMonth', 'input', () => {
            if (callback) {
                callback();
            }
        });
    }

    public expirationMonthFocusEventListener(callback: Function) {
        this.payvault?.card.addEventListener(
            'expirationMonth',
            'focus',
            (event: IPayVaultEvent) => {
                if (callback) {
                    callback(event);
                }
            },
        );
    }

    public expirationMonthBlurEventListener(callback: Function) {
        this.payvault?.card.addEventListener('expirationMonth', 'blur', (event: IPayVaultEvent) => {
            if (callback) {
                callback(event);
            }
        });
    }

    public expirationYearEventListener(callback: Function) {
        this.payvault?.card.addEventListener('expirationYear', 'input', () => {
            if (callback) {
                callback();
            }
        });
    }

    public expirationYearFocusEventListener(callback: Function) {
        this.payvault?.card.addEventListener('expirationYear', 'focus', (event: IPayVaultEvent) => {
            if (callback) {
                callback(event);
            }
        });
    }
    public expirationYearBlurEventListener(callback: Function) {
        this.payvault?.card.addEventListener('expirationYear', 'blur', (event: IPayVaultEvent) => {
            if (callback) {
                callback(event);
            }
        });
    }

    public getBrandName(cardNumber: string): string {
        return this.payvault?.card.cardType(cardNumber);
    }

    public async validateExpiration(): Promise<boolean | undefined> {
        return await this.payvault?.card.validateExpiration();
    }

    public async validateCardNumber(): Promise<boolean | undefined> {
        return await this.payvault?.card.validateCardNumber();
    }

    public async getBrandCode() {
        return await this.payvault?.card.getBrandCode();
    }

    public async requestToken(): Promise<IPayVaultAPISuccessResponse> {
        try {
            const response = await this.payvault?.card.createToken({
                serviceId: this.PAYVAULT_SERVICE_ID,
            });

            if (response.resultType === PayVaultAPIResultType.success) {
                return response as IPayVaultAPISuccessResponse;
            } else if (response.resultType === PayVaultAPIResultType.failure) {
                this.resolveFailureResponse(response);
                return Promise.reject(response.errorMessage);
            } else {
                return Promise.reject('unknown');
            }
        } catch (error) {
            store.dispatch(
                NotificationsActions.showWarning(
                    TranslationHelpers.getFormattedMessage('error.payvault.unavailable'),
                ),
            );
            return Promise.reject('Issue occurred while requesting access token');
        }
    }

    private processTokenCreationResponse(
        status: number,
        response: IPayVaultAPISuccessResponse | IPayVaultAPIFailureResponse,
        successCallback: (response: IPayVaultAPISuccessResponse) => any,
        errorCallback: (reason: string) => any,
    ) {
        if (status === HttpStatus.OK && response.resultType === PayVaultAPIResultType.success) {
            successCallback(response);
        } else if (response.resultType === PayVaultAPIResultType.failure) {
            this.resolveFailureResponse(response);
            errorCallback(response.errorMessage);
        } else {
            errorCallback('unknown');
        }
    }

    private resolveFailureResponse(response: IPayVaultAPIFailureResponse) {
        switch (response.errorCode) {
            case PayVaultAPIErrorCodesConstant.INVALID_REQUEST_PARAMETER:
            case PayVaultAPIErrorCodesConstant.AUTHENTICATION_FAILURE:
            case PayVaultAPIErrorCodesConstant.UNAUTHORIZED_ACCESS:
                store.dispatch(
                    NotificationsActions.showWarning(
                        TranslationHelpers.getFormattedMessage('error.payvault.invalid_details'),
                    ),
                );
                break;

            case PayVaultAPIErrorCodesConstant.UNDER_MAINTENANCE:
            case PayVaultAPIErrorCodesConstant.SYSTEM_ERROR:
                store.dispatch(
                    NotificationsActions.showWarning(
                        TranslationHelpers.getFormattedMessage('error.payvault.unavailable'),
                    ),
                );
                break;

            default:
                ErrorRedirectService.unexpected();
        }
    }
}

export const PayVaultAPIService = new PayVaultAPI();
